import { el, escape } from './dom';
import { PII_LABEL, type PiiType } from '../detect/pii/types';
import type { GeoHit } from '../lib/geocode';
import type { ExifField } from '../detect/exif';
import type { PdfMetaField } from '../detect/pdfMeta';
import type { FaceDetection } from '../detect/faces';
import type { TextDetection } from '../detect/pii/types';
import type { DispatchValue } from '../pipeline/dispatch';
import type { RedactionStore, Entry } from '../lib/redactionStore';
import type { ReviewSelection } from './types';
import type { DetectedLanguage } from '../detect/pii/language';

const truncate = (s: string, max = 64): string => (s.length <= max ? s : s.slice(0, max - 1) + '…');

export type SectionKind = 'text-pii' | 'exif' | 'faces' | 'pdf-meta';

/** What sections this dispatch value can produce, filtered by route. */
const sectionsFor = (
  value: DispatchValue,
  allowed?: ReadonlySet<SectionKind>,
): SectionKind[] => {
  const all: SectionKind[] = [];
  if (value.kind === 'text' || value.kind === 'csv') {
    if (value.detections.length > 0) all.push('text-pii');
  } else if (value.kind === 'image') {
    if (value.exif.fields.length > 0) all.push('exif');
    if (value.faces.length > 0) all.push('faces');
  } else {
    const anyText = value.pages.some((p) => p.detections.length > 0);
    if (anyText) all.push('text-pii');
    if (value.metadata.fields.length > 0) all.push('pdf-meta');
  }
  if (!allowed) return all;
  return all.filter((s) => allowed.has(s));
};

const toggleHtml = (id: string, on: boolean): string =>
  `<div class="pii-toggle${on ? '' : ' off'}" data-toggle-id="${id}"></div>`;

/**
 * Markup for a collapsible section/subgroup header. Each header
 * carries a "toggle all" tri-state checkbox (on/off/partial) and a
 * chevron that collapses the body. The body is `data-collapsed="1"`
 * on the wrapping div when collapsed; CSS hides it.
 */
const groupHeaderHtml = (args: {
  groupId: string;
  label: string;
  /** Optional inline HTML to include beside the label (e.g. the BETA badge). */
  badge?: string;
  ids: readonly string[];
  selectedIds: ReadonlySet<string>;
  /** 'section' for the top-level header, 'subgroup' for nested ones. */
  variant: 'section' | 'subgroup';
  /** Optional override for the count display, e.g. "3 found" instead of bare 3. */
  countSuffix?: string;
}): string => {
  const { groupId, label, badge, ids, selectedIds, variant, countSuffix } = args;
  const total = ids.length;
  const selected = ids.filter((id) => selectedIds.has(id)).length;
  const checkState = selected === 0 ? 'off' : selected === total ? 'on' : 'partial';
  const cls = variant === 'section' ? 'review-section-label' : 'review-group-label';
  const countText = countSuffix ?? `${String(total)} item${total === 1 ? '' : 's'}`;
  return `<div class="${cls} is-collapsible" data-group-id="${escape(groupId)}">
    <button type="button" class="section-toggle-all ${checkState}" data-section-toggle="${escape(groupId)}" aria-label="select all in ${escape(label)}"></button>
    <span class="section-label-text">${escape(label)}${badge ? ` ${badge}` : ''}</span>
    <span class="section-label-count">${escape(countText)}</span>
    <button type="button" class="section-chevron" data-section-chevron="${escape(groupId)}" aria-label="collapse ${escape(label)}">▾</button>
  </div>`;
};

type GroupedText = Map<PiiType, Entry[]>;
const groupTextEntries = (entries: readonly Entry[]): GroupedText => {
  const groups = new Map<PiiType, Entry[]>();
  for (const e of entries) {
    if (e.kind !== 'text') continue;
    const list = groups.get(e.detection.type) ?? [];
    list.push(e);
    groups.set(e.detection.type, list);
  }
  return groups;
};

const renderTextSection = (entries: readonly Entry[], selectedIds: ReadonlySet<string>): string => {
  const groups = groupTextEntries(entries);
  if (groups.size === 0) return '';
  const groupsHtml = Array.from(groups.entries())
    .map(([type, items]) => {
      const itemsHtml = items
        .map((e) => {
          if (e.kind !== 'text') return '';
          const d = e.detection;
          return `
            <div class="pii-item" data-entry-id="${escape(e.id)}">
              <div>
                <div class="pii-item-text">${escape(truncate(d.text))}</div>
                <div class="pii-item-type">${escape(PII_LABEL[type])} · ${(d.confidence * 100).toFixed(0)}%</div>
              </div>
              ${toggleHtml(e.id, selectedIds.has(e.id))}
            </div>`;
        })
        .join('');
      const groupId = `text:${type}`;
      const ids = items.map((e) => e.id);
      return `
        <div class="review-group" data-group="${groupId}">
          ${groupHeaderHtml({ groupId, label: PII_LABEL[type], ids, selectedIds, variant: 'subgroup' })}
          <div class="review-group-body">${itemsHtml}</div>
        </div>`;
    })
    .join('');
  const total = entries.filter((e) => e.kind === 'text').length;
  return `
    <div class="review-section" data-section="text-pii">
      <div class="review-section-summary">PII — ${String(total)} items</div>
      ${groupsHtml}
    </div>`;
};

const renderExifSection = (
  entries: readonly Entry[],
  selectedIds: ReadonlySet<string>,
  geo?: GeoHit,
): string => {
  const items: { entry: Entry; field: ExifField }[] = [];
  for (const e of entries) {
    if (e.kind === 'exif') items.push({ entry: e, field: e.field });
  }
  if (items.length === 0) return '';
  const itemsHtml = items
    .map(({ entry, field }) => {
      const display =
        field.category === 'gps' && geo && (field.key === 'latitude' || field.key === 'longitude')
          ? `${escape(field.display)} <span class="exif-geo">(${escape(geo.label)})</span>`
          : escape(field.display);
      return `
        <div class="pii-item" data-entry-id="${escape(entry.id)}">
          <div>
            <div class="pii-item-text">${escape(field.label)}: ${display}</div>
            <div class="pii-item-type">${escape(field.category)}</div>
          </div>
          ${toggleHtml(entry.id, selectedIds.has(entry.id))}
        </div>`;
    })
    .join('');
  const groupId = 'exif';
  const ids = items.map(({ entry }) => entry.id);
  return `
    <div class="review-section" data-section="exif" data-group="${groupId}">
      ${groupHeaderHtml({ groupId, label: 'METADATA', ids, selectedIds, variant: 'section' })}
      <div class="review-group review-group-body">${itemsHtml}</div>
    </div>`;
};

const renderPdfMetaSection = (
  entries: readonly Entry[],
  selectedIds: ReadonlySet<string>,
): string => {
  const items: { entry: Entry; field: PdfMetaField }[] = [];
  for (const e of entries) {
    if (e.kind === 'pdf-meta') items.push({ entry: e, field: e.field });
  }
  if (items.length === 0) return '';
  const itemsHtml = items
    .map(({ entry, field }) => `
      <div class="pii-item" data-entry-id="${escape(entry.id)}">
        <div>
          <div class="pii-item-text">${escape(field.label)}: ${escape(field.display)}</div>
          <div class="pii-item-type">${escape(field.category)}</div>
        </div>
        ${toggleHtml(entry.id, selectedIds.has(entry.id))}
      </div>`)
    .join('');
  const groupId = 'pdf-meta';
  const ids = items.map(({ entry }) => entry.id);
  return `
    <div class="review-group" data-group="${groupId}">
      ${groupHeaderHtml({ groupId, label: 'METADATA', ids, selectedIds, variant: 'subgroup' })}
      <div class="review-group-body">${itemsHtml}</div>
    </div>`;
};

const renderFacesSection = (
  entries: readonly Entry[],
  selectedIds: ReadonlySet<string>,
): string => {
  const items: { entry: Entry; face: FaceDetection; idx: number }[] = [];
  for (const e of entries) {
    if (e.kind === 'face') items.push({ entry: e, face: e.face, idx: e.index });
  }
  if (items.length === 0) return '';
  const itemsHtml = items
    .map(({ entry, face, idx }) => {
      const thumb = face.thumbnail
        ? `<img class="face-thumb" alt="face ${String(idx + 1)}" src="${face.thumbnail}" />`
        : `<div class="face-thumb face-thumb-placeholder"></div>`;
      return `
        <div class="pii-item face-item" data-entry-id="${escape(entry.id)}">
          <div class="face-row">
            ${thumb}
            <div>
              <div class="pii-item-text">face ${String(idx + 1)}</div>
              <div class="pii-item-type">${(face.confidence * 100).toFixed(0)}% · blur</div>
            </div>
          </div>
          ${toggleHtml(entry.id, selectedIds.has(entry.id))}
        </div>`;
    })
    .join('');
  const groupId = 'faces';
  const ids = items.map(({ entry }) => entry.id);
  return `
    <div class="review-section" data-section="faces" data-group="${groupId}">
      ${groupHeaderHtml({ groupId, label: 'FACES', ids, selectedIds, variant: 'section', countSuffix: `${String(items.length)} found` })}
      <div class="review-group review-group-body">${itemsHtml}</div>
    </div>`;
};

type ManualLike = Entry & { kind: 'manual' | 'manual-text' };

const readLanguage = (store: RedactionStore): DetectedLanguage | undefined => {
  const v = store.value;
  return v.language;
};

const readOcrConfidence = (store: RedactionStore): number | undefined => {
  const v = store.value;
  if (v.kind === 'image') return v.ocrConfidence;
  return undefined;
};

/** True if any image-text entry exists in the dispatch (image OCR or
 *  PDF page OCR). Used to gate the TEXT IN IMAGE section. */
const hasOcrEntries = (store: RedactionStore): boolean =>
  store.entries.some((e) => e.kind === 'image-text');

const renderImageTextSection = (
  entries: readonly Entry[],
  selectedIds: ReadonlySet<string>,
): string => {
  const items = entries.filter(
    (e): e is Entry & { kind: 'image-text' } => e.kind === 'image-text',
  );
  if (items.length === 0) return '';
  const allOnPageZero = items.every((m) => m.pageIndex === 0);
  const itemsHtml = items
    .map((entry) => {
      const pageLabel = allOnPageZero ? '' : ` · page ${String(entry.pageIndex + 1)}`;
      const label = `${escape(truncate(entry.text, 40))}${pageLabel}`;
      const sub = `OCR · ${escape(PII_LABEL[entry.piiType])} · ${(entry.confidence * 100).toFixed(0)}%`;
      return `
        <div class="pii-item ocr-item" data-entry-id="${escape(entry.id)}">
          <div>
            <div class="pii-item-text">${label}</div>
            <div class="pii-item-type">${sub}</div>
          </div>
          ${toggleHtml(entry.id, selectedIds.has(entry.id))}
        </div>`;
    })
    .join('');
  const groupId = 'image-text';
  const ids = items.map((e) => e.id);
  return `
    <div class="review-section" data-section="image-text" data-group="${groupId}">
      ${groupHeaderHtml({
        groupId,
        label: 'TEXT IN IMAGE',
        badge: '<span class="review-section-beta">beta</span>',
        ids,
        selectedIds,
        variant: 'section',
      })}
      <div class="review-group review-group-body">${itemsHtml}</div>
    </div>`;
};

const renderManualSection = (
  entries: readonly Entry[],
  selectedIds: ReadonlySet<string>,
): string => {
  const items = entries.filter(
    (e): e is ManualLike => e.kind === 'manual' || e.kind === 'manual-text',
  );
  if (items.length === 0) return '';
  const allOnPageZero = items.every((m) => m.pageIndex === 0);
  const itemsHtml = items
    .map((entry) => {
      const pageLabel = allOnPageZero ? '' : ` · page ${String(entry.pageIndex + 1)}`;
      const isText = entry.kind === 'manual-text';
      const label = isText
        ? `“${truncate(entry.text, 40)}”${pageLabel}`
        : `manual #${String(entry.seq)}${pageLabel}`;
      const subLabel = isText ? 'highlighted text' : 'drawn rectangle';
      return `
        <div class="pii-item manual-item" data-entry-id="${escape(entry.id)}">
          <div>
            <div class="pii-item-text">${escape(label)}</div>
            <div class="pii-item-type">${subLabel}</div>
          </div>
          <div class="manual-controls">
            ${toggleHtml(entry.id, selectedIds.has(entry.id))}
            <button type="button" class="manual-delete" data-action="manual-delete" data-entry-id="${escape(entry.id)}" aria-label="delete manual redaction">×</button>
          </div>
        </div>`;
    })
    .join('');
  const groupId = 'manual';
  const ids = items.map((e) => e.id);
  return `
    <div class="review-section" data-section="manual" data-group="${groupId}">
      ${groupHeaderHtml({ groupId, label: 'MANUAL', ids, selectedIds, variant: 'section' })}
      <div class="review-group review-group-body">${itemsHtml}</div>
    </div>`;
};

export type ReviewSidebarProps = {
  store: RedactionStore;
  filename: string;
  outputFilename: string;
  notice?: string;
  /** Sections allowed by the current route. undefined = all. */
  allowedSections?: ReadonlySet<SectionKind>;
  geo?: GeoHit;
  onConfirm: (selection: ReviewSelection) => void;
  onCancel: () => void;
  /** Visibility of action buttons. Batch mode hides confirm/cancel. */
  showActions?: boolean;
  /** Custom labels for the action buttons. Used by the batch wizard
   *  so the confirm button says "save & next →" instead of the default
   *  "redact & download → <file>" while staying immune to the
   *  syncConfirmButton relabel that fires on every store emit. */
  confirmLabelHtml?: string;
  cancelLabel?: string;
};

const buildBody = (
  allowed: ReadonlySet<SectionKind> | undefined,
  store: RedactionStore,
  geo?: GeoHit,
): string => {
  const value = store.value;
  const sections = sectionsFor(value, allowed);
  const allowedSet = new Set(sections);
  const selectedIds = store.snapshot().selectedIds;

  let html = '';
  if (allowedSet.has('text-pii')) {
    html += renderTextSection(
      store.entries.filter((e) => e.kind === 'text'),
      selectedIds,
    );
  }
  if (allowedSet.has('exif')) {
    html += renderExifSection(
      store.entries.filter((e) => e.kind === 'exif'),
      selectedIds,
      geo,
    );
  }
  if (allowedSet.has('faces')) {
    html += renderFacesSection(
      store.entries.filter((e) => e.kind === 'face'),
      selectedIds,
    );
  }
  if (allowedSet.has('pdf-meta')) {
    html += renderPdfMetaSection(
      store.entries.filter((e) => e.kind === 'pdf-meta'),
      selectedIds,
    );
  }
  // OCR-derived hits — separate section so they're distinct from
  // EXIF, faces, and the user's own manual rects. Always rendered
  // when present; routes don't currently filter them.
  html += renderImageTextSection(
    store.entries.filter((e) => e.kind === 'image-text'),
    selectedIds,
  );
  // Manuals always render (irrespective of route allowedSections),
  // because the user explicitly drew or highlighted them. If the
  // route forbids the ambient detection kinds, the manuals still
  // ship.
  html += renderManualSection(
    store.entries.filter((e) => e.kind === 'manual' || e.kind === 'manual-text'),
    selectedIds,
  );
  return html;
};

const totalSelectedFor = (
  store: RedactionStore,
  allowed: ReadonlySet<SectionKind> | undefined,
): { total: number; reviewing: number } => {
  const value = store.value;
  const sections = new Set(sectionsFor(value, allowed));
  const inScope = store.entries.filter((e) => {
    if (e.kind === 'manual' || e.kind === 'manual-text') return true;
    if (e.kind === 'image-text') return true;
    if (e.kind === 'text') return sections.has('text-pii');
    if (e.kind === 'face') return sections.has('faces');
    if (e.kind === 'exif') return sections.has('exif');
    return sections.has('pdf-meta');
  });
  const snap = store.snapshot();
  const reviewing = inScope.filter((e) => !snap.selectedIds.has(e.id)).length;
  return { total: inScope.length, reviewing };
};

export type ReviewSidebar = {
  root: HTMLElement;
  dispose(): void;
};

/**
 * Renders the right-side review list. Subscribes to the store: each
 * detection's toggle visually mirrors the store's selection set, and
 * clicking a toggle calls store.toggle(id) — which fires a re-render of
 * the preview canvas via its own subscription.
 *
 * The list itself is rebuilt once on mount; subsequent state changes
 * just flip toggle classes in place. That keeps interaction snappy and
 * preserves scroll position.
 */
export const ReviewSidebar = (props: ReviewSidebarProps): ReviewSidebar => {
  const { store, outputFilename, notice, allowedSections, geo, onConfirm, onCancel } = props;
  const showActions = props.showActions !== false;
  const { total } = totalSelectedFor(store, allowedSections);
  const confirmLabel =
    props.confirmLabelHtml ?? `redact &amp; download → ${escape(outputFilename)}`;
  const cancelLabel = props.cancelLabel ?? 'cancel';

  const noticeHtml = notice ? `<div class="review-notice">${escape(notice)}</div>` : '';
  const actionsHtml = showActions
    ? `
        <div class="review-actions">
          <button type="button" class="review-cancel" data-action="cancel">${escape(cancelLabel)}</button>
          <button type="button" class="review-reset" data-action="reset">reset all</button>
          <button type="button" class="review-confirm" data-action="confirm">${confirmLabel}</button>
        </div>`
    : '';

  const body = buildBody(allowedSections, store, geo);

  const language = readLanguage(store);
  const langBannerHtml = language
    ? `<div class="review-language ${language.supportLevel === 'partial' ? 'is-partial' : ''} mono" data-lang>
         ${
           language.supportLevel === 'partial'
             ? `Detected language: ${escape(language.name)} — partial support, please review carefully`
             : `Detected language: ${escape(language.name)}`
         }
       </div>`
    : '';

  const ocrConf = readOcrConfidence(store);
  const ocrLowConfHtml =
    hasOcrEntries(store) && ocrConf !== undefined && ocrConf < 0.7
      ? `<div class="review-language is-partial mono" data-ocr-warn>
           Text recognition was uncertain — please review the highlighted regions carefully
         </div>`
      : '';

  const nerLoadingHtml =
    store.nerStatus === 'loading'
      ? `<div class="review-language mono" data-ner-loading>
           Loading enhanced detection (one-time, ~110 MB) — additional names and addresses will appear as it finishes…
         </div>`
      : '';

  const root = el(`
    <aside class="review-sidebar" aria-live="polite">
      ${nerLoadingHtml}
      ${langBannerHtml}
      ${ocrLowConfHtml}
      ${noticeHtml}
      <div class="review-frame">
        ${
          total === 0
            ? `<div class="review-empty">No PII detected. The file is already clean — you can download it as-is.</div>`
            : body
        }
      </div>
      ${actionsHtml}
    </aside>
  `);

  // Local UI state for collapsible sections. Survives across re-renders
  // because the source of truth is this Set, not the DOM (we re-apply
  // `data-collapsed` after every rebuildBody).
  const collapsedGroups = new Set<string>();

  /** All entry IDs that belong to a given group/subgroup ID. */
  const idsForGroup = (groupId: string): string[] => {
    if (groupId === 'exif') {
      return store.entries.filter((e) => e.kind === 'exif').map((e) => e.id);
    }
    if (groupId === 'pdf-meta') {
      return store.entries.filter((e) => e.kind === 'pdf-meta').map((e) => e.id);
    }
    if (groupId === 'faces') {
      return store.entries.filter((e) => e.kind === 'face').map((e) => e.id);
    }
    if (groupId === 'image-text') {
      return store.entries.filter((e) => e.kind === 'image-text').map((e) => e.id);
    }
    if (groupId === 'manual') {
      return store.entries
        .filter((e) => e.kind === 'manual' || e.kind === 'manual-text')
        .map((e) => e.id);
    }
    if (groupId.startsWith('text:')) {
      const type = groupId.slice(5);
      return store.entries
        .filter((e) => e.kind === 'text' && e.detection.type === type)
        .map((e) => e.id);
    }
    return [];
  };

  // Click delegation: chevron / section-toggle / delete-X / row-toggle,
  // checked in that order because each later case is a superset of the
  // earlier (everything's inside .review-sidebar).
  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const chevron = target?.closest<HTMLButtonElement>('.section-chevron');
    if (chevron) {
      const groupId = chevron.dataset.sectionChevron;
      if (!groupId) return;
      e.stopPropagation();
      if (collapsedGroups.has(groupId)) collapsedGroups.delete(groupId);
      else collapsedGroups.add(groupId);
      applyCollapseClasses();
      return;
    }
    const sectionToggle = target?.closest<HTMLButtonElement>('.section-toggle-all');
    if (sectionToggle) {
      const groupId = sectionToggle.dataset.sectionToggle;
      if (!groupId) return;
      e.stopPropagation();
      const ids = idsForGroup(groupId);
      if (ids.length === 0) return;
      const selected = store.snapshot().selectedIds;
      const allOn = ids.every((id) => selected.has(id));
      store.setSelectedMany(ids, !allOn);
      return;
    }
    const deleteBtn = target?.closest<HTMLButtonElement>('.manual-delete');
    if (deleteBtn) {
      const id = deleteBtn.dataset.entryId;
      if (!id) return;
      e.stopPropagation();
      store.removeManual(id);
      return;
    }
    const item = target?.closest<HTMLElement>('.pii-item');
    if (!item) return;
    const id = item.dataset.entryId;
    if (!id) return;
    e.stopPropagation();
    store.toggle(id);
  });

  const frameSel = '.review-frame';
  // Rebuild only the scrollable body when entries change — the action
  // buttons live outside the frame now (sticky footer) and never need
  // to be replaced.
  const rebuildBody = (): void => {
    const { total: t } = totalSelectedFor(store, allowedSections);
    const newBody = buildBody(allowedSections, store, geo);
    const frame = root.querySelector<HTMLElement>(frameSel);
    if (!frame) return;
    frame.innerHTML =
      t === 0
        ? `<div class="review-empty">No PII detected. The file is already clean — you can download it as-is.</div>`
        : newBody;
    applyCollapseClasses();
  };

  const syncToggleClasses = (selectedIds: ReadonlySet<string>): void => {
    root.querySelectorAll<HTMLElement>('.pii-toggle').forEach((toggle) => {
      const id = toggle.dataset.toggleId;
      if (!id) return;
      const on = selectedIds.has(id);
      toggle.classList.toggle('off', !on);
    });
  };

  /** Recompute on/off/partial state for every section-toggle button. */
  const syncSectionToggles = (selectedIds: ReadonlySet<string>): void => {
    root.querySelectorAll<HTMLElement>('[data-section-toggle]').forEach((btn) => {
      const groupId = btn.dataset.sectionToggle;
      if (!groupId) return;
      const ids = idsForGroup(groupId);
      if (ids.length === 0) return;
      const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
      const state = selectedCount === 0 ? 'off' : selectedCount === ids.length ? 'on' : 'partial';
      btn.classList.remove('on', 'off', 'partial');
      btn.classList.add(state);
    });
  };

  /** Re-apply data-collapsed to every group container based on the
   *  Set. Called after rebuilds, since innerHTML wipes attributes. */
  const applyCollapseClasses = (): void => {
    root.querySelectorAll<HTMLElement>('[data-group-id]').forEach((header) => {
      const groupId = header.dataset.groupId;
      if (!groupId) return;
      const collapsed = collapsedGroups.has(groupId);
      const container = header.parentElement;
      if (container) container.dataset.collapsed = collapsed ? '1' : '0';
    });
  };

  const idSnapshot = (): string => store.entries.map((e) => e.id).join(',');
  let lastIds = idSnapshot();

  /** Gate the redact button on NER readiness. If the AI pass is still
   *  loading or running, clicking "redact & download" would lock in
   *  an output missing whatever the model is about to surface. Disable
   *  the button and relabel it so the user knows to wait. */
  const syncConfirmButton = (): void => {
    if (!showActions) return;
    const btn = root.querySelector<HTMLButtonElement>('[data-action="confirm"]');
    if (!btn) return;
    if (store.nerStatus === 'loading') {
      btn.disabled = true;
      btn.textContent = 'waiting for enhanced detection…';
    } else {
      btn.disabled = false;
      btn.innerHTML = confirmLabel;
    }
  };

  syncToggleClasses(store.snapshot().selectedIds);
  syncSectionToggles(store.snapshot().selectedIds);
  applyCollapseClasses();
  syncConfirmButton();

  const unsubscribe = store.subscribe((snap) => {
    const ids = idSnapshot();
    if (ids !== lastIds) {
      // entries added or removed (manuals); rebuild the body.
      lastIds = ids;
      rebuildBody();
    }
    syncToggleClasses(snap.selectedIds);
    syncSectionToggles(snap.selectedIds);
    syncConfirmButton();
  });

  function wireActionButtons(): void {
    if (!showActions) return;
    root
      .querySelector<HTMLButtonElement>('[data-action="cancel"]')
      ?.addEventListener('click', onCancel);
    root
      .querySelector<HTMLButtonElement>('[data-action="reset"]')
      ?.addEventListener('click', () => {
        store.resetAll();
      });
    root
      .querySelector<HTMLButtonElement>('[data-action="confirm"]')
      ?.addEventListener('click', () => {
        onConfirm(store.snapshotSelection());
      });
  }
  wireActionButtons();

  return {
    root,
    dispose(): void {
      unsubscribe();
    },
  };
};

// Re-exports useful for callers (e.g. main.ts) building the sidebar.
export type { TextDetection };
